// ── 출퇴근 탭 섹션 ────────────────────────────────────────────────────────────
// 출퇴근 체크 카드 + 출퇴근 기록 카드
// 스타일은 attendanceSectionStyles.js (Tailwind 클래스) 로 분리
import React from 'react';
import AttendanceRecordItem from './AttendanceRecordItem';
import { formatIpWithAlias } from './attendanceIp';
import {
  stackClass, filterRowClass,
  infoLabelClass,
  ipStatusClass, warningBoxClass, warningTitleClass, warningLineClass,
  topCardClass, idBlockClass, avatarClass, avatarImgClass, avatarTextClass,
  nameClass, metaRowClass, metaSepClass,
  ipBlockClass, ipLineClass, ipLabelClass, ipValueClass, locAddrClass, ctaBlockClass,
  actionBtnClass, doneePillClass, moreBtnClass,
  locBoxClass, locLineClass, locValueClass, locHintRowClass, locHintClass, locBtnSmallClass,
  recordHeadClass, recordTitleClass, selectPillClass,
  listWrapClass, emptyTextClass,
} from './attendanceSectionStyles';

export default function AttendanceSection({
  // 오늘 출퇴근 데이터
  today,
  // IP / 위치 정보
  ipAllowed, ipCheckInfo, myIp,
  locationUsable, addressLoading, currentAddress,
  // 로딩 상태
  actionLoading,
  // 출퇴근 기록 목록
  attendanceList, attendanceLoading,
  attendanceYear, attendanceMonth,
  yearOptions, monthOptions,
  expandedAttendance, breakDraftById, savingBreakById,
  // 날짜/시간 포맷 함수 (Dashboard에서 주입)
  fmtDate, weekdayOne, fmtTime,
  getAttendanceSummary,
  // 이벤트 콜백
  onCheckIn, onCheckOut, onRequestLocation,
  onYearChange, onMonthChange,
  onToggleDetail,
  onBreakDraftChange, onSaveBreak,
  // 상단 카드 (2026-09-14 시안)
  profile = null, brandLogoUrl = null, brandName = '',
  // 기록이 있는 연도만. 비어 있으면 기존 yearOptions 로 폴백한다.
  availableYears = [],
}) {
  // 상태 뱃지("출근 전 상태")는 뺐다 (2026-09-14) — 버튼 글자가 같은 말을 하고,
  // 근무 완료는 버튼 자리에 알약으로 보여 준다.

  // 기록은 최신순 5개만 보이고 나머지는 '더보기' (2026-09-14 시안)
  const [showAll, setShowAll] = React.useState(false);
  const PAGE = 5;
  const visibleList = showAll ? attendanceList : attendanceList.slice(0, PAGE);
  const restCount = Math.max(0, attendanceList.length - PAGE);

  // 연도 셀렉트: 기록이 있는 해만. 못 받았으면 기존 목록(폴백).
  //   지금 보고 있는 해는 목록에 없어도 넣는다 — 안 그러면 셀렉트가 빈칸이 된다.
  const yearChoices = (availableYears.length > 0
    ? [...new Set([...availableYears, attendanceYear])]
    : yearOptions).slice().sort((a, b) => b - a);

  // 이름 아래 줄: 부서 · 근무유형 · 등록 IP. **빈 값은 뺀다**(사용자 확정 2026-09-14).
  const registeredIp = formatIpWithAlias(ipCheckInfo.expected_ip?.ip_address, ipCheckInfo.expected_ip?.label);
  const metaParts = [
    profile?.department && { key: 'dept', text: profile.department },
    profile?.attendance_time_type && { key: 'type', text: profile.attendance_time_type },
    registeredIp && registeredIp !== '-' && { key: 'ip', text: `📍 등록 IP ${registeredIp}` },
  ].filter(Boolean);

  // 출퇴근 버튼 활성화 조건
  const canAction = ipAllowed && locationUsable && !addressLoading && !actionLoading;

  return (
    <div className={stackClass}>

      {/* ── 상단 카드 (2026-09-14 시안) ──
          좌: 프로필 아이콘 + 이름 + (부서 · 근무유형 · 등록 IP)
          중: 현재 IP / IP 상태   우: 출근·퇴근 버튼
          ⚠ 직원 정보는 /api/check-ip 에서 온다. 그 엔드포인트는 as_user_id 를 타므로
            관리자가 열람 중이면 **대상 직원** 값이다. (/api/me 는 관리자 본인이라 못 쓴다) */}
      <div className={topCardClass}>
        <div className={idBlockClass}>
          <div className={avatarClass}>
            {brandLogoUrl ? (
              <img src={brandLogoUrl} alt={brandName} className={avatarImgClass} draggable={false} />
            ) : (
              <span className={avatarTextClass}>{String(brandName || profile?.name || '?').trim().charAt(0) || '?'}</span>
            )}
          </div>
          <div className="min-w-0">
            <div className={nameClass}>{profile?.name || '-'}</div>
            <div className={metaRowClass}>
              {/* 빈 값은 아예 뺀다 (사용자 확정) — 부서·근무유형이 없는 직원이 있다 */}
              {metaParts.map((part, i) => (
                <React.Fragment key={part.key}>
                  {i > 0 && <span className={metaSepClass}>|</span>}
                  <span className="truncate">{part.text}</span>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className={ipBlockClass}>
          <div className={ipLineClass}>
            <span className={ipLabelClass}>현재 IP</span>
            <span className={ipValueClass}>{formatIpWithAlias(myIp, ipCheckInfo.matched_ip?.label)}</span>
          </div>
          <div className={ipLineClass}>
            <span className={ipLabelClass}>IP 상태</span>
            <span className={ipStatusClass(ipAllowed)}>{ipAllowed ? '✓ 허용된 위치' : '✗ 허용되지 않은 위치'}</span>
          </div>
          {/* 위치는 **잡혔을 때만** 여기 뜬다 (사용자 지시 2026-09-14).
              못 잡았으면 아래 빨간 박스가 이유와 동의 버튼을 보여 준다. */}
          {locationUsable && (
            <div className={ipLineClass}>
              <span className={ipLabelClass}>현재 위치</span>
              <span className={locAddrClass}>{currentAddress}</span>
            </div>
          )}
        </div>

        <div className={ctaBlockClass}>
          {!today?.checked_in && (
            <button type="button" className={actionBtnClass} onClick={onCheckIn} disabled={!canAction}>
              {actionLoading ? '처리 중...' : '출근하기'}
            </button>
          )}
          {today?.checked_in && !today?.checked_out && (
            <button type="button" className={actionBtnClass} onClick={onCheckOut} disabled={!canAction}>
              {actionLoading ? '처리 중...' : '퇴근하기'}
            </button>
          )}
          {today?.checked_out && (
            <span className={doneePillClass}>
              근무 완료 ✅ {today?.check_in_time || '-'} ~ {today?.check_out_time || '-'}
            </span>
          )}
        </div>
      </div>

      {/* ── 위치를 못 잡았을 때만 ──
          정상일 때는 위 카드의 '현재 위치' 줄로 간다.
          빨간색은 손을 써야 하는 상태에만 쓴다 (사용자 지시 2026-09-14). */}
      {!locationUsable && (
        <div className={locBoxClass}>
          <div className={locLineClass}>
            <span className={infoLabelClass}>현재 위치</span>
            <span className={locValueClass}>
              {addressLoading
                ? '확인 중...'
                : (currentAddress || '위치 권한이 거부되었거나 현재 위치를 확인할 수 없습니다.')}
            </span>
          </div>
          <div className={locHintRowClass}>
            <button
              type="button"
              className={locBtnSmallClass}
              onClick={onRequestLocation}
              disabled={addressLoading || actionLoading}
            >
              {addressLoading ? '위치 확인 중...' : '📍 위치권한 동의하기'}
            </button>
            <span className={locHintClass}>
              위치 확인이 필요합니다. 버튼을 눌러 위치 권한에 동의하면 현재 주소가 표시되고 출근/퇴근 버튼이 활성화됩니다.
            </span>
          </div>
        </div>
      )}

      {/* IP 불일치 경고 — 출근을 막는 이유라 눈에 띄게 남긴다 */}
      {!ipAllowed && (
        <div className={warningBoxClass}>
          <div className={warningTitleClass}>IP가 허용 목록과 일치하지 않습니다.</div>
          {ipCheckInfo.mismatch_message && <div className={warningLineClass}>{ipCheckInfo.mismatch_message}</div>}
          {ipCheckInfo.guidance && <div className={warningLineClass}>{ipCheckInfo.guidance}</div>}
        </div>
      )}

      {/* ── 출퇴근 기록 ── 카드 테두리 없이 제목 + 목록 (2026-09-14 시안) */}
      <section>
        <div className={recordHeadClass}>
          <h3 className={recordTitleClass}>출퇴근 기록</h3>
          <div className={filterRowClass}>
            <select
              className={selectPillClass}
              value={attendanceYear}
              onChange={e => onYearChange(Number(e.target.value))}
            >
              {yearChoices.map(y => <option key={y} value={y}>{y}년</option>)}
            </select>
            <select
              className={selectPillClass}
              value={attendanceMonth}
              onChange={e => onMonthChange(Number(e.target.value))}
            >
              {monthOptions.map(m => <option key={m} value={m}>{m}월</option>)}
            </select>
          </div>
        </div>

        {attendanceLoading ? (
          <div className={emptyTextClass}>불러오는 중...</div>
        ) : (
          <div className={listWrapClass}>
            {attendanceList.length === 0 && (
              <div className={emptyTextClass}>출퇴근 기록이 없습니다.</div>
            )}
            {visibleList.map((row, index) => {
              const key = `${row.date}-${index}`;
              return (
                <AttendanceRecordItem
                  key={key}
                  row={row}
                  itemKey={key}
                  opened={Boolean(expandedAttendance[key])}
                  summary={getAttendanceSummary(row)}
                  fmtDate={fmtDate}
                  weekdayOne={weekdayOne}
                  breakDraftById={breakDraftById}
                  savingBreakById={savingBreakById}
                  onToggleDetail={onToggleDetail}
                  onBreakDraftChange={onBreakDraftChange}
                  onSaveBreak={onSaveBreak}
                />
              );
            })}

            {/* 더보기 — 최신순 5개만 보이고 나머지는 접어 둔다 (2026-09-14 시안) */}
            {!showAll && restCount > 0 && (
              <button type="button" className={moreBtnClass} onClick={() => setShowAll(true)}>
                더보기 ({restCount}건)
              </button>
            )}
            {showAll && attendanceList.length > PAGE && (
              <button type="button" className={moreBtnClass} onClick={() => setShowAll(false)}>
                접기
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
